// Upload optimisation for payment proofs and financial evidence (no extra dependencies: the browser's own canvas).
//
//   const r = await prepareUpload(file);   // → { file, original, final, optimized, note }
//
// Images (JPEG / PNG / WebP, and HEIC where the browser can decode it) are re-encoded as JPEG, aiming at ~150–300 KB, but
// readability wins: the long edge never goes below 1600 px (or the original, if smaller) and JPEG quality never below 0.7,
// so amounts, dates, transaction IDs, signatures and QR codes stay legible. Orientation from the camera is applied.
// The original is kept whenever optimisation fails, isn't smaller, or the file is already small. PDFs: signed ones are never
// touched; in unsigned ones only large photo images are re-compressed (src/lib/pdfCompression.js, loaded on demand), text and
// pages unchanged — the result is used only if it saves at least 20 %.
// Existing uploads and the app's own generated PDFs are never touched — this runs only on files picked for a new upload.

export const OPTIONS = { targetBytes: 300 * 1024, maxEdge: 2000, minEdge: 1600, startQuality: 0.85, minQuality: 0.7 };
const prepared = new WeakSet();                 // files produced here (so a second pass leaves them alone)
export const isPrepared = (f) => !!f && typeof f === "object" && prepared.has(f);

const isPdf = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
const isHeic = (f) => /^image\/(heic|heif)$/i.test(f.type || "") || /\.(heic|heif)$/i.test(f.name || "");
const isRaster = (f) => /^image\/(jpeg|png|webp)$/i.test(f.type || "") || isHeic(f);
export const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const jpgName = (name) => `${(name || "upload").replace(/\.[^.]+$/, "")}.jpg`;

// A PDF that carries a digital signature (/ByteRange + /Sig, or DocMDP / Perms). Signatures are usually appended at the
// end, so the start and the end of the file are both scanned. Unreadable → treated as signed (never modified).
export async function pdfLooksSigned(file) {
    const text = async (from, to) => { const b = new Uint8Array(await file.slice(from, to).arrayBuffer());
        let s = ""; for (let i = 0; i < b.length; i += 8192) s += String.fromCharCode.apply(null, b.subarray(i, i + 8192)); return s; };
    try {
        const MB4 = 4 * 1024 * 1024;
        const t = file.size <= 2 * MB4 ? await text(0, file.size) : (await text(0, MB4)) + (await text(file.size - MB4, file.size));
        return (/\/ByteRange\s*\[/.test(t) && /\/Type\s*\/Sig\b|\/FT\s*\/Sig\b/.test(t)) || /\/DocMDP\b|\/Perms\s*<</.test(t);
    } catch { return true; }
}

// Pure decision logic over an injectable codec (tested with a fake one; the browser codec is below).
export async function prepareUploadWith(file, codec, opts = {}) {
    const o = { ...OPTIONS, ...opts };
    if (!file) return null;
    const keep = (note) => ({ file, original: file.size, final: file.size, optimized: false, note });
    if (isPrepared(file)) return keep("");
    if (isPdf(file)) {
        if (await pdfLooksSigned(file)) return keep("Signed PDF: uploaded exactly as chosen (signature preserved).");
        if (file.size <= o.targetBytes || !codec.pdf) return keep("PDF: uploaded as chosen (pages and text unchanged).");
        let out = null;
        try { out = await codec.pdf(file); } catch { out = null; }
        if (!out || !out.length || out.length > file.size * 0.8) return keep("PDF: no safe saving, uploaded as chosen (pages and text unchanged).");
        const f = new File([out], file.name, { type: "application/pdf", lastModified: Date.now() });
        prepared.add(f);
        return { file: f, original: file.size, final: f.size, optimized: true,
            note: `PDF optimised ${kb(file.size)} → ${kb(f.size)} (pages and text unchanged; only photos re-compressed).` };
    }
    if (!isRaster(file)) return keep("");
    if (!isHeic(file) && file.size <= o.targetBytes) return keep("Already small: uploaded as chosen.");
    let img;
    try { img = await codec.decode(file); } catch { img = null; }
    if (!img || !img.width || !img.height) return keep(isHeic(file) ? "This browser can't read HEIC photos: uploaded as chosen." : "Couldn't optimise this image: uploaded as chosen.");
    try {
        const long = Math.max(img.width, img.height);
        const floorEdge = Math.min(long, o.minEdge);
        let best = null;
        const tryAt = async (edge, q) => {
            const s = edge / long, w = Math.max(1, Math.round(img.width * s)), h = Math.max(1, Math.round(img.height * s));
            const blob = await codec.encode(img, w, h, q);
            if (blob && blob.size > 0 && (!best || blob.size < best.blob.size)) best = { blob, w, h, q };
            return blob;
        };
        // 1) full working size, lowering quality to the floor; 2) if still large, shrink towards the readability floor.
        const edge0 = Math.min(long, o.maxEdge);
        for (let q = o.startQuality; q >= o.minQuality - 1e-9; q = Math.round((q - 0.05) * 100) / 100) {
            const b = await tryAt(edge0, q); if (b && b.size <= o.targetBytes) break;
        }
        for (let edge = edge0 - 200; best.blob.size > o.targetBytes && edge >= floorEdge; edge -= 200) await tryAt(edge, 0.8);
        if (best.blob.size > o.targetBytes && edge0 > floorEdge) await tryAt(floorEdge, o.minQuality);
        img.close?.();
        if (!isHeic(file) && best.blob.size >= file.size * 0.95) return keep("Already efficient: uploaded as chosen.");
        const out = new File([best.blob], jpgName(file.name), { type: "image/jpeg", lastModified: Date.now() });
        prepared.add(out);
        return { file: out, original: file.size, final: out.size, optimized: true,
            note: `Optimised ${kb(file.size)} → ${kb(out.size)} (${best.w}×${best.h}, readable quality).` };
    } catch {
        img.close?.();
        return keep("Couldn't optimise this image: uploaded as chosen.");
    }
}

// Browser codec: decode with EXIF orientation applied; draw on white (transparent PNGs) and encode JPEG.
const browserCodec = {
    async decode(file) {
        if (typeof createImageBitmap === "function") {
            try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* fall back to <img> */ }
        }
        const url = URL.createObjectURL(file);
        try {
            const im = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
            return { width: im.naturalWidth, height: im.naturalHeight, source: im };
        } finally { setTimeout(() => URL.revokeObjectURL(url), 0); }
    },
    async encode(img, w, h, q) {
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        const g = c.getContext("2d"); g.fillStyle = "#fff"; g.fillRect(0, 0, w, h);
        g.imageSmoothingQuality = "high"; g.drawImage(img.source || img, 0, 0, w, h);
        return new Promise((res) => c.toBlob(res, "image/jpeg", q));
    },
    async pdf(file) {   // only for unsigned PDFs over the size target; pdf-lib is loaded here, on demand
        const { optimizePdfBytes, browserPdfImageCodec } = await import("./pdfCompression");
        return optimizePdfBytes(new Uint8Array(await file.arrayBuffer()), browserPdfImageCodec);
    },
};

export const prepareUpload = (file, opts) => prepareUploadWith(file, browserCodec, opts);
// For the upload functions: optimise if the screen didn't already (never twice), always fall back to the original.
export const optimizedForUpload = async (file) => { try { return (await prepareUpload(file))?.file || file; } catch { return file; } };
